import { useQuery } from "@tanstack/react-query"
import { useEffect, useRef } from "react"

import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import extractURLSearchParam from "@utils/extractURLSearchParam"
import fetchObjectListData from "@utils/fetchObjectListData"

import useTable from "@organisms/Table/Table.context"
import ViewBar from "@organisms/ViewBar/ViewBar"

import { LIST_SEARCH_PARAM_ACTIVE_TAB } from "@constants/searchParams"

import { TableViewTabProps } from "./TableViewTab.types"

const activeTabOverride = extractURLSearchParam(LIST_SEARCH_PARAM_ACTIVE_TAB)

export default function TableViewTab(props: TableViewTabProps) {
    const {
        id,
        title,
        phase,
        countEndpoint,
        endpointKwargs,
        listEndpoint,
        isDefaultActiveTabForPhase,
        hide,
        hideCount,
    } = props

    const { activePhase, activeTab, setActiveTab, searchKeywords, filters, setActivePhase } = useTable()
    const { isLoading } = useUser()
    const isDesktop = useIsDesktop()
    const hasSetTabFromUrl = useRef(false)

    const [tabCountEndpoint, tabListEndpoint] = useSlugExtractor([countEndpoint, listEndpoint])

    const { data } = useQuery({
        queryKey: [tabCountEndpoint, endpointKwargs, searchKeywords, filters, title],
        queryFn: () =>
            fetchObjectListData({
                objectName: `${title} Count`,
                endpoint: tabCountEndpoint,
                endpointKwargs: endpointKwargs,
                searchKeywords: searchKeywords,
                filters: filters,
            }),
        staleTime: 60000,
        // Mobile shows only the selected tab's count (in the select trigger), so the other tabs don't fetch theirs.
        enabled: activePhase === phase && !hideCount && (isDesktop || activeTab?.id === id),
    })

    const onSetActiveTab = () => {
        setActivePhase(phase)

        setActiveTab({
            id,
            title,
            phase,
            countEndpoint: tabCountEndpoint,
            endpointKwargs,
            listEndpoint: tabListEndpoint,
        })
    }

    const isUrlActivated = activeTabOverride === title && !hasSetTabFromUrl.current

    const isLegacyActivated = typeof activeTab === "string" && activeTab === title

    const listEndpointHasChanged =
        activeTab?.listEndpoint !== tabListEndpoint &&
        !!activeTab?.listEndpoint &&
        activeTab?.id === id &&
        activeTab?.phase === phase

    const isDefaultActivated = !!isDefaultActiveTabForPhase && activePhase === phase && activeTab?.phase !== phase

    useEffect(() => {
        if (isLoading) {
            return
        }

        const shouldActivateTab = isUrlActivated || isLegacyActivated || isDefaultActivated || listEndpointHasChanged

        if (shouldActivateTab) {
            onSetActiveTab()

            hasSetTabFromUrl.current = true
        }
    }, [
        isLoading,
        activeTabOverride,
        id,
        activeTab,
        title,
        isDefaultActiveTabForPhase,
        activePhase,
        phase,
        onSetActiveTab,
        listEndpointHasChanged,
    ])

    return (
        <ViewBar.Tab
            value={id}
            label={title}
            phase={phase}
            count={hideCount ? undefined : data?.count}
            isSelected={activeTab?.id === id || isDefaultActivated}
            isHidden={hide}
            onSelect={onSetActiveTab}
        />
    )
}
